import { useEffect, useState } from "react"

/**
 * The height that makes an element reach the bottom of the window.
 *
 * ── Why this is measured rather than written down ───────────────────────────
 *
 * The chat page used `calc(100vh - 230px)`, and 230 was the height of everything
 * above it on the day it was written. Then a timer bar appeared above the page
 * when a timer is running, and a warning banner appears for an account with no
 * role — and the constant was wrong in three different ways depending on what
 * else was on screen. A number that describes the rest of the layout goes stale
 * the moment the rest of the layout changes.
 *
 * So it asks. The element's own distance from the top of the viewport is a fact
 * the browser already knows, and it is re-read whenever anything could have
 * moved it.
 *
 * Returns a CSS length for `height`, or `undefined` before the first measurement
 * — callers should fall back to something sensible for that single frame.
 */
export function useFillViewport(ref, { gap = 24, minimum = 320 } = {}) {
  const [height, setHeight] = useState()

  useEffect(() => {
    const node = ref.current
    if (!node) return undefined

    const measure = () => {
      const top = node.getBoundingClientRect().top
      setHeight(`${Math.max(minimum, Math.round(window.innerHeight - top - gap))}px`)
    }

    measure()
    window.addEventListener("resize", measure)

    /*
     * Anything above this element changing size moves it, and a resize event is
     * not fired for that — the timer bar sliding in is a layout change inside
     * the document, not a window resize. Observing the body catches it.
     */
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure)
    observer?.observe(document.body)

    return () => {
      window.removeEventListener("resize", measure)
      observer?.disconnect()
    }
  }, [ref, gap, minimum])

  return height
}
